import { useState, useMemo, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../../utils/amplifyClient';
import * as Sentry from '@sentry/browser';

import { useUserContext } from '../../contexts/UserContext';
import { useUserModule, UserModuleProvider } from '../../contexts/UserModuleContext';
import { UserEditModal } from '../UserEditModal';
import { computeUserStats, filterUsersBySearch, filterUsersByRole, paginateUsers } from '../../utils/userModuleUtils';
import { RoleFilter } from '../../types/userModuleTypes';
import {
    Role,
    ROLE_PERMISSIONS,
    Permission,
    canManageCompanyUser,
    getCompanyOwnerEmailFromDirectory,
} from '../../utils/permissions';
import { NestedOption } from '../../types';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { CreateUserInput, UpdateUserInput, User } from '../../types/schema';
import {
    createUser as createUserMutation,
    deleteUser as deleteUserMutation,
    updateUser as updateUserMutation,
} from '../../graphql/mutations';
import { NETWORK_DOMAIN } from '../../utils/constants';
import {
    removeCompanyDirectoryMember,
    sortCompanyUserDirectory,
} from '../../utils/companyUserDirectory';
import { isUnlinkedInvite } from '../../utils/preferredCompany';
import {
    assignmentsToNestedOptions,
    buildRegionDistrictLocationOptionGroups,
    nestedOptionsToAssignments,
} from '../../utils/regionDistrictOptions';
import { Card } from '../ui/cards';
import { DeleteUserModal } from './DeleteUserModal';

import '../../css/UsersListPage.css';
import {
    BtnIconOnly,
    DataTable,
    IconEmail,
    IconListSquare,
    IconPencil,
    IconTrash,
    PageHeader,
    Pagination,
} from '../ui';
import type { DataTableColumn } from '../ui';

const PAGE_SIZE = 10;

const stopRowClick = (event: MouseEvent) => {
    event.stopPropagation();
};

const ROLE_FILTERS: { label: string; value: RoleFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Admin', value: 'admin' },
    { label: 'Inspector', value: 'inspector' },
    { label: 'Operator', value: 'operator' },
    { label: 'Unconfirmed', value: 'unconfirmed' },
];

interface UsersStatCardProps {
    value: number;
    label: string;
}

const UsersStatCard = ({ value, label }: UsersStatCardProps) => (
    <>
        <div className="ulp-stat-card-value">{value}</div>
        <div className="ulp-stat-card-label">{label}</div>
    </>
);

/**
 * Inner component that uses UserModuleContext
 */
const UsersListPageInner = () => {
    const navigate = useNavigate();
    const { company, companyOwner, companyUser, businessAdmin, allCompanyUsers, setAllCompanyUsers, setCompany } =
        useUserContext();
    const { skillAssignments } = useUserModule();
    const { companyRegions, companyLocations, companyDistricts } = useRegionAndLocationContext();

    const companyId = (company?.id as string) || companyOwner?.companyId;

    // State
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<RoleFilter>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Add User modal state
    const [formName, setFormName] = useState('');
    const [formEmail, setFormEmail] = useState('');
    const [selectedRole, setSelectedRole] = useState<Role | ''>('');
    const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
    const [selectedRegionOptions, setSelectedRegionOptions] = useState<NestedOption[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState<string | null>(null);

    // All company users including the owner
    const managedUsers = useMemo(() => {
        return sortCompanyUserDirectory(allCompanyUsers, company?.companyOwnerId);
    }, [allCompanyUsers, company?.companyOwnerId]);

    const companyOwnerEmail = useMemo(
        () => getCompanyOwnerEmailFromDirectory(allCompanyUsers, company?.companyOwnerId),
        [allCompanyUsers, company?.companyOwnerId],
    );

    // Compute stats
    const stats = useMemo(() => {
        return computeUserStats(managedUsers, skillAssignments);
    }, [managedUsers, skillAssignments]);

    // Apply search filter
    const searchedUsers = useMemo(() => {
        return filterUsersBySearch(managedUsers, searchQuery);
    }, [managedUsers, searchQuery]);

    // Apply role filter
    const filteredUsers = useMemo(() => {
        return filterUsersByRole(searchedUsers, activeFilter);
    }, [searchedUsers, activeFilter]);

    // Paginate
    const paginatedResult = useMemo(() => {
        return paginateUsers(filteredUsers as User[], currentPage, PAGE_SIZE);
    }, [filteredUsers, currentPage]);

    // Region/Location options for the UserEditModal
    const regionLocationOptions = useMemo(
        () =>
            buildRegionDistrictLocationOptionGroups({
                companyId: companyId || '',
                companyRegions,
                companyDistricts,
                companyLocations,
            }),
        [companyRegions, companyLocations, companyDistricts, companyId],
    );

    // Handlers
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleFilterChange = (filter: RoleFilter) => {
        setActiveFilter(filter);
        setCurrentPage(1);
    };

    const handleRowClick = useCallback((userId: string) => {
        navigate(`/user/${userId}`);
    }, [navigate]);

    const [resendingUserId, setResendingUserId] = useState<string | null>(null);

    const handleResendInvite = useCallback(
        async (user: User) => {
            if (resendingUserId) return;
            setResendingUserId(user.id);
            try {
                // Delete and recreate the user to re-trigger the DynamoDB stream INSERT → email lambda
                const userData = {
                    email: user.email,
                    domain: NETWORK_DOMAIN,
                    name: user.name,
                    companyId: user.companyId,
                    role: user.role,
                    permissions: user.permissions,
                    discrepancyNotification: user.discrepancyNotification || false,
                    discrepancyNotificationTypes:
                        user.discrepancyNotificationTypes || '{ "inop": false, "warn": false }',
                    regionalAssignment: user.regionalAssignment || [],
                    locationAssignment: user.locationAssignment || [],
                    globalLocationPermission: user.globalLocationPermission || false,
                };

                await authClient.graphql({
                    query: deleteUserMutation,
                    variables: { input: { id: user.id } },
                });

                const response = await authClient.graphql({
                    query: createUserMutation,
                    variables: { input: userData },
                });

                const newUser = (response as { data: { createUser: User } }).data.createUser;
                if (newUser) {
                    setAllCompanyUsers(
                        allCompanyUsers.map((u) => (u.id === user.id ? newUser : u)),
                    );
                }
                alert(`Invitation email resent to ${user.email}`);
            } catch (error) {
                console.error('Failed to resend invite:', error);
                Sentry.captureException(error);
                alert('Failed to resend invitation. Please try again.');
            } finally {
                setResendingUserId(null);
            }
        },
        [allCompanyUsers, resendingUserId, setAllCompanyUsers],
    );

    const handleDeleteUser = async () => {
        if (!deleteConfirmUser) return;

        // Never delete the signed-in account from this screen.
        if (
            deleteConfirmUser.id === companyOwner?.id ||
            deleteConfirmUser.id === companyUser?.id
        ) {
            alert('You cannot delete the account you are currently signed in with.');
            setDeleteConfirmUser(null);
            return;
        }

        setIsDeleting(true);
        try {
            const result = await removeCompanyDirectoryMember({
                userId: deleteConfirmUser.id,
                companyId: companyId || company?.id,
                companyOwnerId: company?.companyOwnerId,
                replacementOwnerId: companyOwner?.id || companyUser?.id || null,
            });

            const updatedUsers = allCompanyUsers.filter((u) => u.id !== deleteConfirmUser.id);
            setAllCompanyUsers(updatedUsers);

            if (result.reassignedCompanyOwnerId && company) {
                setCompany({
                    ...company,
                    companyOwnerId: result.reassignedCompanyOwnerId,
                });
            }

            setDeleteConfirmUser(null);
        } catch (error) {
            console.error('Failed to delete user:', error);
            Sentry.captureException(error);
            alert('Failed to delete user. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleFormSubmit = async (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (!companyId) return;
        setIsSubmitting(true);

        try {
            const { regionalAssignment, locationAssignment, globalLocationPermission: hasAllAccess } =
                nestedOptionsToAssignments(selectedRegionOptions, {
                    companyId: companyId || '',
                    companyRegions,
                    companyLocations,
                });

            if (!isEditing) {
                const input: CreateUserInput = {
                    email: formEmail.trim().toLowerCase(),
                    domain: NETWORK_DOMAIN,
                    name: formName.trim(),
                    companyId,
                    role: selectedRole || 'admin',
                    permissions: JSON.stringify(selectedPermissions),
                    discrepancyNotification: false,
                    discrepancyNotificationTypes: `{ "inop": false, "warn": false }`,
                    regionalAssignment,
                    locationAssignment,
                    globalLocationPermission: hasAllAccess,
                };
                const response = (await authClient.graphql({
                    query: createUserMutation,
                    variables: { input },
                })) as { data?: { createUser?: User | null } };
                const newUser = response.data?.createUser;
                if (newUser) {
                    setAllCompanyUsers([...allCompanyUsers, newUser]);
                }
            } else {
                const input: UpdateUserInput = {
                    id: isEditing,
                    email: formEmail.trim().toLowerCase(),
                    domain: NETWORK_DOMAIN,
                    name: formName.trim(),
                    companyId,
                    role: selectedRole || 'admin',
                    permissions: JSON.stringify(selectedPermissions),
                    regionalAssignment,
                    locationAssignment,
                    globalLocationPermission: hasAllAccess,
                };
                const response = (await authClient.graphql({
                    query: updateUserMutation,
                    variables: { input },
                })) as { data?: { updateUser?: User | null } };
                const updatedUser = response.data?.updateUser;
                if (updatedUser) {
                    // Merge response with existing user data to preserve fields not returned by mutation
                    setAllCompanyUsers(
                        allCompanyUsers.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)),
                    );
                }
            }

            // Reset form
            resetForm();
        } catch (error) {
            console.error('Failed to submit user form:', error);
            Sentry.captureException(error);
        } finally {
            setIsSubmitting(false);
            setIsAddUserModalOpen(false);
            setIsEditing(null);
        }
    };

    const resetForm = () => {
        setFormName('');
        setFormEmail('');
        setSelectedRole('');
        setSelectedPermissions([]);
        setSelectedRegionOptions([]);
    };

    const openAddUserModal = () => {
        resetForm();
        setIsEditing(null);
        setIsAddUserModalOpen(true);
    };

    const openEditUserModal = useCallback(
        (user: User) => {
            if (
                !canManageCompanyUser(
                    companyUser,
                    companyOwner,
                    user,
                    company?.companyOwnerId,
                    companyOwnerEmail,
                    businessAdmin,
                )
            ) {
                return;
            }
            setFormName(user.name || '');
            setFormEmail(user.email || '');
            setSelectedRole((user.role as Role) || '');
            try {
                setSelectedPermissions(user.permissions ? JSON.parse(user.permissions) : []);
            } catch {
                setSelectedPermissions([]);
            }

            setSelectedRegionOptions(
                assignmentsToNestedOptions(user, {
                    companyId: companyId || '',
                    companyRegions,
                    companyDistricts,
                    companyLocations,
                }),
            );
            setIsEditing(user.id);
            setIsAddUserModalOpen(true);
        },
        [
            businessAdmin,
            company?.companyOwnerId,
            companyDistricts,
            companyId,
            companyLocations,
            companyOwner,
            companyOwnerEmail,
            companyRegions,
            companyUser,
        ],
    );

    // Helpers
    const getInitials = (name: string | null | undefined): string => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return (parts[0][0] || '?').toUpperCase();
    };

    const getRoleBadgeClass = (role: string | null | undefined): string => {
        if (!role) return 'default';
        const lower = role.toLowerCase();
        if (lower === 'owner' || lower === 'super-admin') return 'owner';
        if (lower.includes('admin')) return 'admin';
        if (lower.includes('operator')) return 'operator';
        if (lower.includes('inspector')) return 'inspector';
        return 'default';
    };

    const formatRoleName = (role: string | null | undefined): string => {
        if (!role) return '—';
        const lower = role.toLowerCase();
        if (lower === 'owner') return 'Owner';
        if (lower === 'super-admin') return 'Super Admin';
        return role
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    };

    const renderUserActions = useCallback(
        (user: User) => {
            const canManageRow = canManageCompanyUser(
                companyUser,
                companyOwner,
                user,
                company?.companyOwnerId,
                companyOwnerEmail,
                businessAdmin,
            );
            const protectedActionTitle = 'You cannot edit or delete users at or above your role level';
            const displayName = user.name?.trim() || 'user';

            return (
                <div className="table-actions" onClick={stopRowClick}>
                    <BtnIconOnly
                        size="sm"
                        icon={<IconListSquare />}
                        ariaLabel={`View ${displayName}`}
                        onClick={() => handleRowClick(user.id)}
                    />
                    <BtnIconOnly
                        size="sm"
                        icon={<IconPencil />}
                        ariaLabel={canManageRow ? `Edit ${displayName}` : protectedActionTitle}
                        buttonTooltip={canManageRow ? undefined : protectedActionTitle}
                        disabled={!canManageRow}
                        onClick={() => openEditUserModal(user)}
                    />
                    <BtnIconOnly
                        size="sm"
                        icon={<IconEmail />}
                        ariaLabel={user.email ? `Message ${displayName}` : `${displayName} has no email`}
                        disabled={!user.email}
                        onClick={() => {
                            if (user.email) {
                                window.location.href = `mailto:${user.email}`;
                            }
                        }}
                    />
                    <BtnIconOnly
                        size="sm"
                        variant="danger"
                        icon={<IconTrash />}
                        ariaLabel={canManageRow ? `Delete ${displayName}` : protectedActionTitle}
                        buttonTooltip={canManageRow ? undefined : protectedActionTitle}
                        disabled={!canManageRow}
                        onClick={() => setDeleteConfirmUser(user)}
                    />
                </div>
            );
        },
        [
            businessAdmin,
            company?.companyOwnerId,
            companyOwner,
            companyOwnerEmail,
            companyUser,
            handleRowClick,
            openEditUserModal,
        ],
    );

    const userTableColumns = useMemo<DataTableColumn<User>[]>(
        () => [
            {
                id: 'user',
                header: 'User',
                mobileCardPrimary: true,
                renderCell: (user) => (
                    <div className="ulp-user-cell">
                        <div className="ulp-avatar">{getInitials(user.name)}</div>
                        <div className="ulp-user-info">
                            <span className="ulp-user-name">{user.name || '—'}</span>
                            <span className="ulp-user-email">{user.email || '—'}</span>
                        </div>
                    </div>
                ),
            },
            {
                id: 'role',
                header: 'Role',
                mobileCardStacked: true,
                renderCell: (user) => (
                    <span className={`ulp-role-badge ${getRoleBadgeClass(user.role)}`}>
                        {formatRoleName(user.role)}
                    </span>
                ),
            },
            {
                id: 'jobClassification',
                header: 'Job Classification',
                mobileCardStacked: true,
                renderCell: (user) => user.currentJobClassification || '—',
            },
            {
                id: 'safetyEnrollment',
                header: 'Safety Enrollment',
                mobileCardStacked: true,
                renderCell: (user) => (
                    <span
                        className={`ulp-enrollment-pill ${
                            user.trainingModuleEnrollment ? 'enrolled' : 'not-enrolled'
                        }`}
                    >
                        {user.trainingModuleEnrollment ? 'Enrolled' : 'Not Enrolled'}
                    </span>
                ),
            },
            {
                id: 'skillsets',
                header: 'Skillsets',
                mobileCardStacked: true,
                renderCell: (user) => {
                    const assigned = skillAssignments.some((a) => a.userId === user.id);
                    return (
                        <span className={`ulp-skillset-pill ${assigned ? 'assigned' : 'not-assigned'}`}>
                            {assigned ? 'Assigned' : 'Not assigned'}
                        </span>
                    );
                },
            },
            {
                id: 'status',
                header: 'Status',
                mobileCardStacked: true,
                renderCell: (user) => {
                    const unlinked = isUnlinkedInvite(user.cognitoId);
                    return (
                        <span
                            className={`ulp-status-pill ${unlinked ? 'unconfirmed' : 'confirmed'}`}
                            onClick={(event) => {
                                if (unlinked) {
                                    event.stopPropagation();
                                    handleResendInvite(user);
                                }
                            }}
                            title={unlinked ? 'Click to resend invitation email' : undefined}
                        >
                            {unlinked ? '↻ Resend Invite' : 'Confirmed'}
                        </span>
                    );
                },
            },
            {
                id: 'actions',
                header: 'Actions',
                headerLabel: 'Actions',
                align: 'center',
                thClassName: 'data-table-col-narrow',
                tdClassName: 'data-table-col-narrow',
                mobileCardAction: true,
                mobileHideLabel: true,
                renderCell: (user) => renderUserActions(user),
            },
        ],
        [handleResendInvite, renderUserActions, skillAssignments],
    );

    const getUserStatCards = (currentStats: {
        totalUsers: number;
        confirmed: number;
        unconfirmed: number;
        skillsetsAssigned: number;
    }) => [
        { value: currentStats.totalUsers, label: 'Total Users' },
        { value: currentStats.confirmed, label: 'Confirmed' },
        { value: currentStats.unconfirmed, label: 'Unconfirmed' },
        { value: currentStats.skillsetsAssigned, label: 'Skillsets Assigned' },
    ];

    return (
        <div className="page-container users-list-page">
            {/* Header */}
            <PageHeader
                title="Users"
                signalLine="Manage team members, roles, and qualifications"
                actions={
                    <>
                        <button className="ulp-btn-outlined" onClick={() => navigate('/user-skillsets')}>
                            User Skillsets
                        </button>
                        <button className="ulp-btn-primary" onClick={openAddUserModal}>
                            + Add User
                        </button>
                    </>
                }
            />

            {/* Stats Cards */}
            <div className="ulp-stats-row">
                {getUserStatCards(stats).map(({ value, label }) => (
                    <Card key={label} className="ulp-stat-card">
                        <UsersStatCard value={value} label={label} />
                    </Card>
                ))}
            </div>

            {/* Search + Filter */}
            <div className="ulp-filter-bar">
                <div className="ulp-search-row">
                    <input
                        type="text"
                        className="ulp-search-input"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>
                <div className="ulp-filter-chips">
                    {ROLE_FILTERS.map((filter) => (
                        <button
                            key={filter.value}
                            className={`ulp-chip ${activeFilter === filter.value ? 'active' : ''}`}
                            onClick={() => handleFilterChange(filter.value)}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Users Table */}
            <div className="ulp-table-container">
                <DataTable
                    className="ulp-users-table"
                    caption="Company users"
                    captionVisuallyHidden
                    ariaLabel="Company users"
                    columns={userTableColumns}
                    rows={paginatedResult.items}
                    getRowKey={(user) => user.id}
                    onRowClick={(user) => handleRowClick(user.id)}
                    hideHeaderWhenEmpty
                    emptyState={
                        <div className="ulp-empty-state">
                            {searchQuery || activeFilter !== 'all'
                                ? 'No users match your search or filter criteria.'
                                : "No users found. Click '+ Add User' to get started."}
                        </div>
                    }
                />

                {paginatedResult.items.length > 0 && (
                    <div className="ulp-pagination">
                        <div className="ulp-pagination-info">
                            Showing{' '}
                            {Math.min((paginatedResult.currentPage - 1) * PAGE_SIZE + 1, filteredUsers.length)}{' '}
                            - {Math.min(paginatedResult.currentPage * PAGE_SIZE, filteredUsers.length)} of{' '}
                            {filteredUsers.length} users
                        </div>
                        {paginatedResult.totalPages > 1 && (
                            <Pagination
                                currentPage={paginatedResult.currentPage}
                                totalPages={paginatedResult.totalPages}
                                onPageChange={setCurrentPage}
                                ariaLabel="Users pagination"
                            />
                        )}
                    </div>
                )}
            </div>

            <DeleteUserModal
                user={deleteConfirmUser}
                isDeleting={isDeleting}
                isListedCompanyOwner={deleteConfirmUser?.id === company?.companyOwnerId}
                onDismiss={() => setDeleteConfirmUser(null)}
                onConfirmDelete={handleDeleteUser}
            />

            {/* Add/Edit User Modal */}
            <UserEditModal
                visible={isAddUserModalOpen}
                isEditing={!!isEditing}
                isSubmitting={isSubmitting}
                formName={formName}
                formEmail={formEmail}
                selectedRole={selectedRole}
                selectedPermissions={selectedPermissions}
                selectedRegionOptions={selectedRegionOptions}
                regionLocationOptions={regionLocationOptions}
                onNameChange={setFormName}
                onEmailChange={setFormEmail}
                onRoleChange={(role) => {
                    setSelectedRole(role);
                    const rolePerms = ROLE_PERMISSIONS[role] || [];
                    setSelectedPermissions(rolePerms);
                }}
                onPermissionsChange={setSelectedPermissions}
                onRegionOptionsChange={setSelectedRegionOptions}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                    setIsAddUserModalOpen(false);
                    setIsEditing(null);
                    resetForm();
                }}
            />
        </div>
    );
};

/**
 * UsersListPage wraps the inner component with UserModuleProvider
 */
export const UsersListPage = () => {
    return (
        <UserModuleProvider>
            <UsersListPageInner />
        </UserModuleProvider>
    );
};
